/**
 * wikiPlainText — turns a wiki page's stored EditorJS content into the plain text of an SMS message.
 *
 * Used by Appointment Reminders → Settings → "Switch to a plain message": the office picked a wiki page for a
 * text slot and now wants that same wording in the slot's own message box (docs/plans/2026-10-17-inline-reminder-
 * messages-plan.md, D5). It mirrors what teamio-utility `getContentFromWikiTemplate` does to an SMS template, so
 * the box shows what patients are texted today:
 *   - header blocks are the page TITLE and are skipped (utility skips them for SMS too);
 *   - every paragraph is one line; HTML is stripped, entities are decoded, `&nbsp;` becomes a space;
 *   - an empty paragraph produces nothing (utility renders no blank line for it either).
 * Two small deliberate differences: a `<br>` becomes a line break (utility drops it, which glues the words
 * together), and a page made ONLY of header blocks falls back to those headers (utility has the same
 * "header-only rescue" — prod had templates typed entirely as a heading).
 *
 * LINKS (docs/plans/2026-10-19-online-scheduling-link-token-plan.md, WP3c): a plain box cannot hold a hyperlink, and
 * the converter used to drop every <a> and keep only its words, so an office switching a recall page with a booking
 * link silently lost it. Now, per anchor (the display text is the label; an anchor with no label is dropped, exactly
 * as the sender's processLink drops it; data-real-href wins over href, as there):
 *   - a link to the booking page (path /online-scheduling/book-appointment.html, relative or absolute, ANY host) in a
 *     slot that may hold the online-scheduling link (`opts.allowBookingLink`) becomes "<label> {{online_scheduling_link}}"
 *     and the result carries `bookingLink: { aptTypeId, label }` (the `apt` query parameter or null; the label is the
 *     appointment type's name when `opts.aptTypes` lists it, else ''). The URL itself is never kept: a page copied from a
 *     host template carries the HOST's siteID. The FIRST booking link decides which page the (one) token opens;
 *   - the same link anywhere else becomes "<label>: <absolute URL>" (`opts.origin` is put in front of a relative one);
 *   - any other http(s) link becomes "<label>: <url>" (what the sender produces; just the URL when the label IS the url);
 *   - a form / packet / payment link ({{patient_link_for_form_…}}, {{patient_link_for_packet_…}}, payment_link) cannot be
 *     carried: only its label stays and the result carries `hadFormOrPaymentLink: true`;
 *   - every other href (mailto:, tel:, a relative path) keeps just its label, as before.
 * Called without `opts` the output is what it always was, except for those links. The extra keys (`bookingLink`,
 * `hadFormOrPaymentLink`, `formLink`) are on the result only when they apply.
 *
 * FORM LINK (Forms Settings → Medical History Update, docs/plans/2026-10-20-forms-settings-plan.md D13): with `opts.allowFormLink` the FIRST
 * {{patient_link_for_form_<id>}} / {{patient_link_for_packet_<id>}} anchor becomes "<label> {{form_link}}" (the slot's one Form link) and the result
 * carries `formLink: { kind: 'form'|'packet', id, name }` — the name looked up in `opts.forms` ([{ formIDForWeb, name }]) / `opts.packets`
 * ([{ packetID, name }]), `formLink: null` when the id is in neither (the box then says "no form picked yet"). Any LATER form / packet link keeps
 * today's behaviour (the label only + hadFormOrPaymentLink). Without the option nothing changes.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (cypress/node-tests/apt-reminder-inline-messages.test.js executes it verbatim).
 */

const NAMED_ENTITIES = {
  nbsp: ' ',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  rsquo: '’',
  lsquo: '‘',
  rdquo: '”',
  ldquo: '“',
  ndash: '–',
  mdash: '—',
  hellip: '…',
};

/** One pass, so `&amp;lt;` decodes to the text `&lt;` (not `<`). Unknown entities are left as written. */
function decodeEntities(text) {
  return String(text).replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z][a-z0-9]*);/gi, (whole, body) => {
    if (body.charAt(0) === '#') {
      const isHex = body.charAt(1).toLowerCase() === 'x';
      const code = parseInt(body.slice(isHex ? 2 : 1), isHex ? 16 : 10);
      if (!isFinite(code) || code <= 0 || code > 0x10ffff) return whole;
      return code === 160 ? ' ' : String.fromCodePoint(code);
    }
    const named = NAMED_ENTITIES[body.toLowerCase()];
    return named === undefined ? whole : named;
  });
}

// The booking page every office's online scheduling lives at, and the token that stands for "a link to YOUR booking page"
// (both agree with reminderMessageDefaults.js BOOKING_PAGE_PATH / BOOKING_LINK_TOKEN — a node-test pins it; this file
// stays import-free).
const BOOKING_PAGE_PATH = '/online-scheduling/book-appointment.html';
const BOOKING_TOKEN = '{{online_scheduling_link}}';
// Forms Settings: the token that stands for "the form / packet this message links to" (agrees with utils/formsSettings/formLink.js FORM_LINK_TOKEN —
// a node-test pins it; this file stays import-free). The anchors a wiki page uses for it carry {{patient_link_for_form_<id>}} /
// {{patient_link_for_packet_<id>}} in href or data-real-href (utility.js getShortUrlForForm / ForPacket).
const FORM_LINK_TOKEN = '{{form_link}}';
const FORM_LINK_HREF_RE = /patient_link_for_(form|packet)_([^}\s]+)/;

/** The { kind, id, name } a converted form link opened, named from the site's lists; null when the id is not in them. */
function lookupFormLink(kind, id, opts) {
  const list = kind === 'packet' ? opts.packets : opts.forms;
  const key = kind === 'packet' ? 'packetID' : 'formIDForWeb';
  const hit = (Array.isArray(list) ? list : []).find(item => item && String(item[key]) === id);
  return hit ? { kind, id, name: typeof hit.name === 'string' ? hit.name : '' } : null;
}

// <a …>label</a> plus the one punctuation mark the sender also takes along (processLink).
const ANCHOR_RE = /<a\b([^>]*)>([\s\S]*?)<\/a>([:;,.!]?)/gi;
// Private-use markers: an anchor's final text is parked behind one while tags are stripped and entities decoded (so a
// URL is never decoded twice), then put back.
const MARK_OPEN = '\uE000';
const MARK_CLOSE = '\uE001';
const MARK_RE = new RegExp(MARK_OPEN + '(\\d+)' + MARK_CLOSE, 'g');

/** One attribute's value out of an <a …> attribute string ('' when absent). */
function attrOf(attrs, name) {
  const m = new RegExp('(?:^|\\s)' + name + '\\s*=\\s*(?:"([^"]*)"|\'([^\']*)\'|([^\\s>]+))', 'i').exec(attrs);
  if (!m) return '';
  return m[1] !== undefined ? m[1] : (m[2] !== undefined ? m[2] : m[3]);
}

/** The URL an anchor points at: data-real-href (the wiki editor keeps form / packet ids there) wins over href. */
function hrefOf(attrs) {
  const real = attrOf(attrs, 'data-real-href');
  if (real) {
    const decoded = decodeEntities(real);
    try { return decodeURIComponent(decoded).trim(); } catch (e) { return decoded.trim(); }
  }
  return decodeEntities(attrOf(attrs, 'href')).trim();
}

/**
 * Is this href the booking page? The path is compared (case-insensitive, trailing slashes tolerated) after the scheme
 * and host are removed, so a relative link and an absolute one on any host both count.
 * @returns {{isBooking: boolean, aptTypeId: (string|null)}}
 */
function parseBookingHref(href) {
  const noHost = String(href).replace(/^(?:[a-z][a-z0-9+.-]*:)?\/\/[^/?#]*/i, '');
  const path = noHost.split(/[?#]/)[0].replace(/\/+$/, '').toLowerCase();
  if (path !== BOOKING_PAGE_PATH) return { isBooking: false, aptTypeId: null };
  const query = (noHost.split('#')[0].split('?')[1]) || '';
  let aptTypeId = null;
  query.split('&').forEach((pair) => {
    const eq = pair.indexOf('=');
    if (eq === -1 || pair.slice(0, eq) !== 'apt' || aptTypeId !== null) return;
    let value = pair.slice(eq + 1);
    try { value = decodeURIComponent(value); } catch (e) { /* keep it as written */ }
    value = value.trim();
    if (value !== '') aptTypeId = value;
  });
  return { isBooking: true, aptTypeId };
}

/** A relative / protocol-relative href as an absolute URL (`origin` in front of a path); anything else as written. */
function absoluteUrl(href, origin) {
  if (/^\/\//.test(href)) return 'https:' + href;
  if (/^\//.test(href)) return String(origin || '').replace(/\/+$/, '') + href;
  return href;
}

/** What one anchor turns into (see the LINKS note in the header). `ctx` collects the link facts for the result. */
function anchorText(attrs, inner, punct, ctx) {
  const label = decodeEntities(String(inner).replace(/<[^>]*>/g, '')).replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
  if (label === '') return ''; // an invisible link: the sender drops it too
  const href = hrefOf(attrs);
  const opts = ctx.opts;
  const colon = punct === ':';
  const tail = punct && !colon ? punct : '';

  if (/patient_link_for_(?:form|packet)|payment_link/.test(href)) {
    // Forms Settings (opts.allowFormLink): the FIRST form / packet link becomes "<label> {{form_link}}" and the result carries which form it opened.
    // Later ones (and every payment link) keep the label only, as ever.
    const formHit = opts.allowFormLink === true && !ctx.formLinkDone ? FORM_LINK_HREF_RE.exec(href) : null;
    if (formHit) {
      ctx.formLinkDone = true;
      ctx.formLink = lookupFormLink(formHit[1], formHit[2], opts);
      if (/^https?:\/\//i.test(label)) return FORM_LINK_TOKEN + tail; // the label was the URL itself
      return label + (colon ? ': ' : ' ') + FORM_LINK_TOKEN + tail;
    }
    ctx.hadFormOrPaymentLink = true;
    return label + punct;
  }
  const booking = parseBookingHref(href);
  if (booking.isBooking && opts.allowBookingLink === true) {
    if (!ctx.bookingLink) {
      const known = (Array.isArray(opts.aptTypes) ? opts.aptTypes : []).find(t => t && booking.aptTypeId !== null && t.id === booking.aptTypeId);
      ctx.bookingLink = { aptTypeId: booking.aptTypeId, label: known && typeof known.name === 'string' ? known.name : '' };
    }
    if (/^https?:\/\//i.test(label)) return BOOKING_TOKEN + tail; // the label was the URL itself
    return label + (colon ? ': ' : ' ') + BOOKING_TOKEN + tail;
  }
  const url = booking.isBooking ? absoluteUrl(href, opts.origin) : (/^https?:\/\//i.test(href) ? href : '');
  if (url === '') return label + punct; // mailto:, tel:, a relative path, no href: the words, as before
  if (label === url) return url + punct;
  return label + ': ' + url + tail;
}

/** One block's `data.text` as plain text: <br> to a line break, links per the LINKS note, tags stripped, entities decoded. */
function blockText(html, ctx) {
  const context = ctx || { opts: {} };
  const parked = [];
  const marked = String(html == null ? '' : html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(ANCHOR_RE, (whole, attrs, inner, punct) => {
      const text = anchorText(attrs, inner, punct, context);
      return text === '' ? '' : MARK_OPEN + (parked.push(text) - 1) + MARK_CLOSE;
    })
    .replace(/<[^>]*>/g, '');
  return decodeEntities(marked)
    .replace(/\u00a0/g, ' ')
    .replace(MARK_RE, (whole, i) => parked[Number(i)])
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .join('\n');
}

/** The page's message blocks as one string per paragraph (a header-only page falls back to its headers). */
function paragraphsOf(content, ctx) {
  let doc = content;
  if (typeof doc === 'string') {
    try { doc = JSON.parse(doc); } catch (e) { return []; }
  }
  const blocks = doc && Array.isArray(doc.blocks) ? doc.blocks : [];

  const collect = (type) => blocks
    .filter(b => b && b.type === type && b.data)
    .map(b => blockText(b.data.text, ctx))
    .filter(Boolean);

  const paragraphs = collect('paragraph');
  return paragraphs.length === 0 ? collect('header') : paragraphs; // header-only page: the whole message was typed as a heading
}

/** The converters' result: ok + text, plus the link facts only when they apply (so a link-free page is {ok, text}). */
function resultOf(text, ctx) {
  const result = { ok: text !== '', text };
  if (result.ok && ctx.bookingLink) result.bookingLink = ctx.bookingLink;
  if (result.ok && ctx.hadFormOrPaymentLink) result.hadFormOrPaymentLink = true;
  if (result.ok && ctx.formLinkDone) result.formLink = ctx.formLink; // null when the page's form / packet is not in opts.forms / opts.packets
  return result;
}

/**
 * @param {string|object} content the page's `Content` column (EditorJS JSON, usually a string)
 * @param {{allowBookingLink?: boolean, origin?: string, aptTypes?: Array<{id: string, name: string}>,
 *          allowFormLink?: boolean, forms?: Array<{formIDForWeb: string, name: string}>, packets?: Array<{packetID: string, name: string}>}} [opts]
 *        see the LINKS / FORM LINK notes in the file header (allowBookingLink: the slot may hold {{online_scheduling_link}}; origin: put in
 *        front of a relative booking link; aptTypes: the site's appointment types, to name the one a link opens; allowFormLink + forms +
 *        packets: the Forms Settings form link)
 * @returns {{ok: boolean, text: string, bookingLink?: {aptTypeId: (string|null), label: string}, hadFormOrPaymentLink?: boolean,
 *            formLink?: ({kind: string, id: string, name: string}|null)}}
 *          ok is false (and text '') when the content cannot be read or holds no message text, so the caller can fall
 *          back to the default and say so.
 */
function wikiContentToPlainText(content, opts) {
  const ctx = { opts: opts || {} };
  return resultOf(paragraphsOf(content, ctx).join('\n'), ctx);
}

// The office signature is appended by the email layout and is never typed into a message (plan D8). Hosted email
// pages end with this token (wiki-service bakes it as the triple-brace form for SendGrid).
const SIGNATURE_TOKEN = /\{\{\{?\s*email_signature_block\s*\}?\}\}/gi;
// SendGrid's unescaped-HTML form of a variable ({{{confirm_button}}}); the inline email layout takes the double form.
const TRIPLE_BRACE_TOKEN = /\{\{\{\s*([A-Za-z0-9_]+)\s*\}\}\}/g;

/** One line of an email page: the signature token removed (the rest of the line stays), triple braces made double. */
function emailLine(line) {
  const hadSignature = new RegExp(SIGNATURE_TOKEN.source, 'i').test(line);
  const cleaned = line.replace(SIGNATURE_TOKEN, ' ').replace(TRIPLE_BRACE_TOKEN, '{{$1}}');
  return hadSignature ? cleaned.replace(/[ \t]{2,}/g, ' ').trim() : cleaned.trim();
}

/**
 * An EMAIL wiki page as the body of a plain email message ("Switch to a plain message" on an email slot). Like
 * wikiContentToPlainText, but paragraphs stay APART (a blank line between them: the inline email layout turns a blank
 * line into a new <p> and a single line break into <br>, which is how the hosted layout drew the page's paragraphs),
 * the office signature token is dropped (the layout appends the signature itself), and the SendGrid triple-brace form
 * of a variable becomes the plain {{double}} form the layout understands.
 *
 * Links are handled exactly as in wikiContentToPlainText (the inline email layout auto-links a bare URL).
 *
 * @param {string|object} content the page's `Content` column
 * @param {{allowBookingLink?: boolean, origin?: string, aptTypes?: Array<{id: string, name: string}>}} [opts] as for
 *        wikiContentToPlainText
 * @returns {{ok: boolean, text: string, bookingLink?: {aptTypeId: (string|null), label: string}, hadFormOrPaymentLink?: boolean}}
 *          ok is false (and text '') when nothing but the signature, or no text at all, is left.
 */
function wikiContentToEmailText(content, opts) {
  const ctx = { opts: opts || {} };
  const text = paragraphsOf(content, ctx)
    .map(paragraph => paragraph.split('\n').map(emailLine).filter(Boolean).join('\n'))
    .filter(Boolean)
    .join('\n\n');
  return resultOf(text, ctx);
}

export { decodeEntities, wikiContentToPlainText, wikiContentToEmailText };
